import { useMemo, useState } from "react";
import { ArrowDownUp, ArrowUpRight, CalendarClock, Search } from "lucide-react";
import { Badge, Button, Dialog, DialogActions, FilterBar, SearchField, Select, Table } from "@studio";
import { HeatmapChart, KpiCard, KpiGrid, SeriesChart, ShareDonut, Sparkline, tone } from "../charts";
import { SeoNotice } from "@studio-features/meta-crew/seo-shared";
import { errorMessage } from "../lib/api";
import { fmtDateTime, num } from "../lib/format";
import { useAdminSeoKeywords, useCustomerSeoKeywords, useCustomers } from "../lib/queries";
import { useSeoConnections, useSeoHistory } from "../lib/seo-api";
import {
  AREA_ORDER,
  RANK_NOTICE,
  analyticsModel,
  areaName,
  kstDay,
  kstTime,
  lastValue,
  metricDelta,
  rowKey,
  topN,
  useSeoAnalyticsData,
  type AnalyticsModel,
} from "../lib/analytics-model";
import type { SeoKeyword } from "../lib/types";
import { Empty, ErrorBox, Loading, PanelError, Panel, Tabs } from "../console/ui";

/**
 * 분석 화면 (시안 4572e49 seo-analytics.tsx MarketingAnalytics 를 실제 API 로).
 * - 고객 웹 "분석": 자기 테넌트. 관리자 "전체 분석": 고객사 선택(전체 고객사 합산 포함).
 * - 데이터는 코어 수집기가 모은 네이버 검색 결과 (GET /c/:id/seo/daily · posts). 차트는 콘솔 공통 ECharts 부품(src/charts).
 * - 차트 고르기: 날짜별 개수 분포 = 누적 막대(수집일이 하루뿐이어도 보임), 순위 = 1위가 위인 선, 순위 변동 = 상승·하락 색 가로 막대, 점유 = 도넛.
 * - 검색 수요·구글 검색·비즈니스 프로필은 수집기가 없어 빈 상태만 둔다. 미수집 수치는 그리지 않는다.
 */

const STATUS_LABEL: Record<SeoKeyword["status"], string> = { tracking: "추적 중", stopped: "중지", ended: "종료" };

type KeywordRow = SeoKeyword & { customerId?: string; customerName?: string };
/** 분석 행 = 고객사 · 키워드 · 영역. */
type AnRow = { key: string; customerId: string; customerName?: string; keywordId: string; keyword: string; area: string; ref: KeywordRow };

const rowName = (r: AnRow, total: boolean) => `${total && r.customerName ? `${r.customerName} · ` : ""}${r.keyword} · ${areaName(r.area)}`;

function buildRows(keywords: KeywordRow[], model: AnalyticsModel, customerIdOf: (k: KeywordRow) => string | null): AnRow[] {
  const seen = new Map<string, Set<string>>();
  for (const r of model.daily) {
    const k = `${r.customerId}|${r.keywordId}`;
    if (!seen.has(k)) seen.set(k, new Set());
    seen.get(k)!.add(r.area);
  }
  return keywords.flatMap((k) => {
    const customerId = customerIdOf(k);
    if (!customerId) return [];
    const areas = [...(seen.get(`${customerId}|${k.id}`) ?? (k.areas?.length ? k.areas : ["blog"]))].sort((a, b) => AREA_ORDER.indexOf(a) - AREA_ORDER.indexOf(b));
    return areas.map((area) => ({ key: rowKey({ customerId, keywordId: k.id, area }), customerId, customerName: k.customerName, keywordId: k.id, keyword: k.keyword, area, ref: k }));
  });
}

export function AnalyticsEmpty({ kind = "empty" }: { kind?: string }) {
  const title = kind === "empty" ? "아직 분석 중인 키워드가 없습니다" : kind === "pending" ? "첫 수집을 기다리고 있습니다" : `${kind} 데이터 수집 준비 중`;
  return (
    <div className="la-empty">
      <span>
        <Search size={24} />
      </span>
      <h3>{title}</h3>
      <p>
        {kind === "empty"
          ? "담당자가 키워드를 등록하면 검색 성과를 확인할 수 있습니다."
          : kind === "pending"
            ? "첫 수집 전 · 수집 주기에 맞춰 자동으로 채워집니다."
            : "연결 정보는 보관되어 있습니다. 수집이 시작되면 이곳에 분석 결과가 표시됩니다."}
      </p>
      {kind === "검색 수요" && <small>PC·모바일 월간 검색 수 · 데이터랩 상대 지수 · 기기·성별·연령</small>}
      {kind === "구글 검색" && <small>클릭 · 노출 · 클릭률 · 평균 게재순위</small>}
      {kind === "비즈니스 프로필" && <small>프로필 조회 · 웹사이트 클릭 · 전화 · 경로 요청</small>}
      <Badge tone="neutral">{kind === "empty" ? "키워드 없음" : kind === "pending" ? "수집 예정" : "아직 수집된 데이터 없음"}</Badge>
    </div>
  );
}

function Metrics({ model, compare = true, selected, onSelect, summary = false }: { model: AnalyticsModel; compare?: boolean; selected?: number; onSelect?: (n: number) => void; summary?: boolean }) {
  return (
    <KpiGrid>
      {(summary ? [1, 2, 0] : [0, 1, 2, 3]).map((i) => {
        const s = model.metrics[i]!;
        const v = lastValue(s.values).value;
        return (
          <KpiCard
            key={s.name}
            label={s.name}
            value={v == null ? "—" : `${v}${i === 3 ? "%" : ""}`}
            values={s.values}
            delta={compare ? metricDelta(s, i === 3 ? "%p" : "개") : undefined}
            active={selected === i}
            onClick={onSelect ? () => onSelect(i) : undefined}
          />
        );
      })}
    </KpiGrid>
  );
}

function Movers({ model, rows, total, compact = false, onDetail }: { model: AnalyticsModel; rows: AnRow[]; total: boolean; compact?: boolean; onDetail?: (r: AnRow) => void }) {
  const byKey = new Map(rows.map((r) => [r.key, r]));
  const label = (m: (typeof model.moves)[number]) => {
    const r = byKey.get(rowKey(m.row));
    return r ? rowName(r, total) : `${m.row.keyword} · ${areaName(m.row.area)}`;
  };
  const up = model.moves.filter((m) => m.delta != null && m.delta > 0).sort((a, b) => b.delta! - a.delta!).slice(0, compact ? 3 : 5);
  const down = model.moves.filter((m) => m.delta != null && m.delta < 0).sort((a, b) => a.delta! - b.delta!).slice(0, compact ? 3 : 5);
  const moves = [...up, ...down];
  const item = (m: (typeof model.moves)[number], body: React.ReactNode) => {
    const r = byKey.get(rowKey(m.row));
    return (
      <p key={rowKey(m.row)}>
        {onDetail && r ? (
          <button className="seo-text-button" onClick={() => onDetail(r)}>
            {label(m)}
          </button>
        ) : (
          <span>{label(m)}</span>
        )}
        {body}
      </p>
    );
  };
  return (
    <div className="la-movers">
      {!compact && !moves.length && (
        <Panel title="오늘의 순위 변동">
          <p className="la-caption">{model.lastDay ? `${model.lastDay} 수집과 비교할 직전 수집일이 없거나, 순위가 바뀐 키워드가 없습니다.` : "수집된 결과가 없습니다."}</p>
        </Panel>
      )}
      {!compact && moves.length > 0 && (
        <SeriesChart
          title="오늘의 순위 변동"
          kind="bar"
          horizontal
          showValues
          format={(n) => (n == null ? "—" : n > 0 ? `↑ ${n}` : n < 0 ? `↓ ${-n}` : "0")}
          labels={moves.map(label)}
          series={[{ name: "직전 수집 대비 순위 변동", values: moves.map((m) => m.delta), pointColors: moves.map((m) => (m.delta! > 0 ? tone.ok : tone.fail)) }]}
          description={`초록 = 상승 · 빨강 = 하락 · 같은 키워드·영역의 직전 수집일과 비교${model.lastDay ? ` · 기준 ${model.lastDay}` : ""}`}
        />
      )}
      <div className="la-move-lists">
        {compact &&
          (
            [
              ["상승", up],
              ["하락", down],
            ] as const
          ).map(([title, list]) => (
            <div key={title}>
              <h4>{title}</h4>
              {list.length ? list.map((m) => item(m, <b className={m.delta! > 0 ? "la-up" : "la-down"}>{`${m.delta! > 0 ? "↑" : "↓"} ${Math.abs(m.delta!)}`}</b>)) : <p>변동 없음 · 비교 자료 필요</p>}
            </div>
          ))}
        {(
          [
            ["신규 진입", model.moves.filter((m) => m.isNew)],
            ["상위 밖으로 이탈", model.moves.filter((m) => m.left)],
          ] as const
        ).map(([title, list]) => (
          <div key={title}>
            <h4>{title}</h4>
            {list.length ? list.slice(0, 3).map((m) => item(m, <small>{m.isNew ? `${m.row.bestRank}위` : "상위 밖"}</small>)) : <p>해당 키워드 없음</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

function PublishedArticles({ model }: { model: AnalyticsModel }) {
  const posts = [...model.posts].sort((a, b) => b.postedAt.localeCompare(a.postedAt));
  const matches = (customerId: string, word: string) => model.current.filter((r) => r.keyword === word && r.customerId === customerId);
  return (
    <Panel title="게시 글별 키워드 노출" extra={<Badge>{posts.length}건</Badge>}>
      <p className="la-caption">이 고객사를 대상으로 게시된 글 · 게시 사실과 검색 결과를 함께 확인합니다.</p>
      {posts.length ? (
        <Table>
          <thead>
            <tr>
              <th>게시 글 / 키워드</th>
              <th>게시 시각</th>
              <th>현재 노출</th>
            </tr>
          </thead>
          <tbody>
            {posts.map((p, i) => (
              <tr key={`${p.customerId}-${p.postedAt}-${i}`}>
                <td>
                  <details>
                    <summary>{p.title}</summary>
                    <div className="la-post-keywords">
                      {p.url && (
                        <p>
                          <a href={p.url} target="_blank" rel="noreferrer">
                            게시 글 열기
                          </a>
                        </p>
                      )}
                      {p.keywords.map((word) => {
                        const m = matches(p.customerId, word);
                        return (
                          <p key={word}>
                            {word} <strong>{m.length ? m.map((r) => `${areaName(r.area)} ${r.found && r.bestRank != null ? `${r.bestRank}위` : "상위 밖"}`).join(" · ") : "미수집"}</strong>
                          </p>
                        );
                      })}
                      {!p.keywords.length && <p>연결된 키워드 없음</p>}
                    </div>
                  </details>
                </td>
                <td>
                  {kstDay(p.postedAt)} {kstTime(p.postedAt)}
                </td>
                <td>
                  {p.keywords.filter((w) => matches(p.customerId, w).some((r) => r.found)).length} / {p.keywords.length}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <p className="la-caption">기간 내 게시된 글이 없습니다.</p>
      )}
    </Panel>
  );
}

/* ---------------- 분석 본문 ---------------- */

export function SeoAnalyticsScreen({
  customerId,
  admin = false,
  staffView = false,
  onManage,
}: {
  /** 고객 웹: 자기 고객사. 관리자: 처음 고른 고객사 (null = 전체 고객사). */
  customerId: string | null;
  admin?: boolean;
  staffView?: boolean;
  onManage?: (customerId: string) => void;
}) {
  const [selected, setSelected] = useState<string>(customerId ?? "all");
  const [days, setDays] = useState(30);
  const [tab, setTab] = useState("개요");
  const [compare, setCompare] = useState(false);
  const [metric, setMetric] = useState<number | undefined>();
  const [query, setQuery] = useState("");
  const [area, setArea] = useState("전체");
  const [more, setMore] = useState(false);
  const [log, setLog] = useState(false);
  const [chosen, setChosen] = useState<string[] | null>(null);
  const [detail, setDetail] = useState<AnRow | null>(null);
  const customers = useCustomers("", admin);
  const scopeId = admin ? (selected === "all" ? null : selected) : customerId;
  const total = admin && !scopeId;

  const custKeywords = useCustomerSeoKeywords(!admin && customerId ? customerId : "");
  const adminKeywords = useAdminSeoKeywords(admin ? (scopeId ? { customerId: scopeId } : {}) : { customerId: "none" });
  const keywordsQ = admin ? adminKeywords : custKeywords;
  const keywords = useMemo(() => (keywordsQ.data?.items ?? []) as KeywordRow[], [keywordsQ.data]);
  const customerIdOf = (k: KeywordRow) => (admin ? (k.customerId ?? scopeId) : customerId);
  const ids = useMemo(
    () => (admin ? (scopeId ? [scopeId] : [...new Set(keywords.map((k) => k.customerId).filter((x): x is string => !!x))]) : customerId ? [customerId] : []),
    [admin, scopeId, customerId, keywords],
  );
  const data = useSeoAnalyticsData(ids, days);
  const model = useMemo(() => analyticsModel(data.daily, data.posts, days), [data.daily, data.posts, days]);
  const rows = useMemo(() => buildRows(keywords, model, customerIdOf), [keywords, model, admin, scopeId, customerId]); // eslint-disable-line react-hooks/exhaustive-deps
  const live = rows.filter((r) => r.ref.status === "tracking");
  const picked = chosen ?? live.slice(0, 2).map((r) => r.key);
  const matches = (r: AnRow) => r.keyword.includes(query) && (area === "전체" || r.area === area);
  const visible = live.filter(matches);
  const loading = keywordsQ.isLoading || data.loading;
  const error = keywordsQ.error ?? data.error;
  const tabs = ["개요", "키워드 순위", "검색 영역", "검색 수요", "구글 검색", "비즈니스 프로필"];
  const active = tabs.includes(tab) ? tab : "개요";
  const areasSeen = AREA_ORDER.filter((a) => rows.some((r) => r.area === a));
  const metricSeries = metric == null ? model.bands : [{ ...model.metrics[metric]!, previous: compare ? model.metrics[metric]!.previous : undefined }];
  // 히트맵은 첫 수집일부터 (최소 7칸) — 수집 초기에 빈 칸만 가득한 표를 피한다
  const firstIdx = model.labels.findIndex((d) => model.at(d).length);
  const heatFrom = firstIdx < 0 ? 0 : Math.max(0, Math.min(firstIdx, model.labels.length - 7));
  const sumOn = (keys: string[], f: (d: string) => number | null) => keys.map(f);
  const ourOn = (day: string, a?: string) => {
    const rs = model.at(day).filter((r) => !a || r.area === a);
    return rs.length ? rs.reduce((n, r) => n + r.ourCount, 0) : null;
  };

  return (
    <div className="la-stack">
      <div className="la-toolbar">
        <FilterBar>
          {admin && (
            <Select
              label="고객사"
              value={selected}
              onChange={(e) => {
                setSelected(e.target.value);
                setTab("개요");
                setDetail(null);
                setChosen(null);
              }}
            >
              <option value="all">전체 고객사</option>
              {(customers.data?.items ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          )}
          <Select label="조회 기간" value={String(days)} onChange={(e) => setDays(Number(e.target.value))}>
            {[7, 30, 90].map((n) => (
              <option key={n} value={n}>
                최근 {n}일
              </option>
            ))}
          </Select>
          <label className="la-check">
            <input type="checkbox" checked={compare} onChange={(e) => setCompare(e.target.checked)} />
            이전 기간과 비교
          </label>
          {staffView && onManage && scopeId && (
            <Button variant="secondary" size="sm" onClick={() => onManage(scopeId)}>
              SEO 관리
            </Button>
          )}
        </FilterBar>
        <p>
          마지막 수집 {model.lastCollectedAt ? fmtDateTime(model.lastCollectedAt) : "—"}
          <span>확정된 마지막 날짜 {model.lastDay ?? "—"} · KST</span>
        </p>
      </div>
      <Tabs
        items={tabs}
        value={active}
        onChange={(next) => {
          setTab(next);
          setArea("전체");
        }}
      />
      <p className="la-caption">
        {RANK_NOTICE} · 지역 검색은 상위 5개로 별도 집계합니다.{total && " 고객사별 키워드를 합산합니다."}
      </p>
      {["검색 수요", "구글 검색", "비즈니스 프로필"].includes(active) ? (
        <AnalyticsEmpty kind={active} />
      ) : error ? (
        <ErrorBox error={errorMessage(error)} onRetry={() => (void keywordsQ.refetch(), data.refetch())} />
      ) : loading ? (
        <Loading />
      ) : !live.length && !rows.length ? (
        <AnalyticsEmpty />
      ) : !data.daily.length ? (
        <AnalyticsEmpty kind="pending" />
      ) : (
        <>
          {active === "개요" && (
            <>
              <Metrics model={model} compare={compare} selected={metric} onSelect={(n) => setMetric(metric === n ? undefined : n)} />
              <SeriesChart
                key={`main${metric}`}
                title={metric == null ? "순위 구간별 키워드 추이" : `${model.metrics[metric]!.name} 추이`}
                kind={metric == null ? "bar" : "line"}
                stacked={metric == null}
                format={metric === 3 ? "percent" : "count"}
                labels={model.labels}
                series={metricSeries}
                markers={model.markers}
                description={metric == null ? "수집된 키워드만 집계 · 지역 검색 제외 · 세로선은 게시 시각" : "카드를 선택하면 해당 지표의 추이를 확인할 수 있습니다"}
              />
              {metric != null && (
                <button className="seo-text-button la-reset" onClick={() => setMetric(undefined)}>
                  순위 구간별 분포로 보기
                </button>
              )}
              {compare && metric == null && <SeriesChart title="상위 100 노출 · 이전 기간 비교" labels={model.labels} series={[model.metrics[1]!]} description="실선 = 이번 기간 · 점선 = 이전 기간의 같은 순서 날짜" />}
              <div className="la-two">
                <SeriesChart title="오늘의 순위 분포" kind="bar" labels={model.bands.map((s) => s.name)} series={[{ name: "키워드 수", values: model.bands.map((s) => (model.lastDay ? (s.values[model.labels.indexOf(model.lastDay)] ?? null) : null)), pointColors: model.bands.map((b) => (b.color === tone.muted ? tone.muted : null)) }]} description={model.lastDay ? `${model.lastDay} 수집 기준 · 지역 제외` : undefined} />
                <Movers model={model} rows={rows} total={total} onDetail={setDetail} />
              </div>
              <PublishedArticles model={model} />
            </>
          )}
          {active === "키워드 순위" && (
            <>
              <FilterBar>
                <SearchField label="키워드 검색" showLabel value={query} onChange={(e) => setQuery(e.target.value)} placeholder="키워드 이름" />
                <Select label="검색 영역" value={area} onChange={(e) => setArea(e.target.value)}>
                  <option value="전체">전체</option>
                  {AREA_ORDER.map((a) => (
                    <option key={a} value={a}>
                      {areaName(a)}
                    </option>
                  ))}
                </Select>
              </FilterBar>
              <HeatmapChart
                rank
                title="키워드 × 날짜 순위 히트맵"
                columns={model.labels.slice(heatFrom)}
                rows={visible.slice(0, more ? undefined : 5).map((r) => ({ name: rowName(r, total), values: model.history(r.key).slice(heatFrom) }))}
                description="색이 진할수록 상위 순위 · 지역 검색은 5위 이내"
              />
              {visible.length > 5 && (
                <Button variant="secondary" size="sm" onClick={() => setMore(!more)}>
                  {more ? "접기" : `키워드 더 보기 (${visible.length}개)`}
                </Button>
              )}
              <div className="la-compare-select">
                <strong>
                  키워드 비교 <small>{picked.length} / 5</small>
                </strong>
                <div>
                  {live.map((r) => (
                    <label key={r.key}>
                      <input
                        type="checkbox"
                        checked={picked.includes(r.key)}
                        disabled={!picked.includes(r.key) && picked.length >= 5}
                        onChange={(e) => setChosen(e.target.checked ? [...picked, r.key] : picked.filter((k) => k !== r.key))}
                      />
                      {rowName(r, total)}
                    </label>
                  ))}
                </div>
              </div>
              <SeriesChart
                title="키워드 순위 비교"
                labels={model.labels}
                series={live
                  .filter((r) => picked.includes(r.key))
                  .map((r) => ({
                    name: rowName(r, total),
                    values: model.historyRaw(r.key),
                    missing: model.history(r.key).map((v) => (v === 101 ? "상위 밖" : null)),
                    previous: compare ? model.historyRaw(r.key, model.previousLabels) : undefined,
                  }))}
                rank
                markers={model.markers}
                description="최대 5개 · 1위가 위 · 상위 밖·미수집은 선을 잇지 않음 · 점선 = 이전 기간"
              />
              <RankTable rows={rows.filter(matches)} model={model} total={total} days={days} onDetail={setDetail} />
            </>
          )}
          {active === "검색 영역" && (
            <>
              <div className="la-two">
                <SeriesChart
                  title="영역별 노출률"
                  kind="bar"
                  horizontal
                  max={100}
                  format="percent"
                  labels={areasSeen.map((a) => (a === "local" ? "지역 · 상위 5" : areaName(a)))}
                  series={[
                    {
                      name: "노출률",
                      values: areasSeen.map((a) => {
                        const r = model.current.filter((r) => r.area === a);
                        return r.length ? Math.round((r.filter((r) => r.found).length / r.length) * 100) : null;
                      }),
                    },
                  ]}
                  description="노출 키워드 / 수집된 추적 키워드 · 마지막 수집일 기준"
                />
                <SeriesChart title="영역별 우리 항목 수" description="날짜별 · 막대 높이 = 전체 우리 항목" kind="bar" stacked labels={model.labels} series={areasSeen.map((a) => ({ name: areaName(a), values: sumOn(model.labels, (d) => ourOn(d, a)) }))} />
              </div>
              {compare && (
                <SeriesChart
                  title="우리 항목 수 · 이전 기간 비교"
                  description="실선 = 이번 기간 · 점선 = 이전 기간"
                  labels={model.labels}
                  series={[{ name: "우리 항목 수", values: sumOn(model.labels, (d) => ourOn(d)), previous: sumOn(model.previousLabels, (d) => ourOn(d)) }]}
                />
              )}
              <HeatmapChart
                rank
                title="영역 × 키워드 최고 순위"
                columns={areasSeen.map(areaName)}
                rows={[...new Map(visible.map((r) => [`${r.customerId}|${r.keywordId}`, r])).values()].map((r) => ({
                  name: `${total && r.customerName ? `${r.customerName} · ` : ""}${r.keyword}`,
                  values: areasSeen.map((a) => {
                    const x = model.current.find((c) => c.customerId === r.customerId && c.keywordId === r.keywordId && c.area === a);
                    return x ? (x.found && x.bestRank != null ? x.bestRank : 101) : null;
                  }),
                }))}
                description="빈 칸: 해당 영역 수집 없음 · 지역은 상위 5 기준 · 마지막 수집일 기준"
              />
              <label className="la-check">
                <input type="checkbox" checked={log} onChange={(e) => setLog(e.target.checked)} />
                결과 건수 로그 축
              </label>
              <SeriesChart
                title="영역별 검색 결과 전체 건수"
                kind="bar"
                format="compact"
                log={log}
                labels={areasSeen.map(areaName)}
                series={[
                  {
                    name: "결과 건수",
                    values: areasSeen.map((a) => {
                      const r = model.current.filter((r) => r.area === a);
                      return r.length ? r.reduce((n, r) => n + r.total, 0) : null;
                    }),
                  },
                ]}
                description="키워드별 결과 건수의 합 · 중복 문서를 포함할 수 있음 · 검색 수요가 아닙니다"
              />
            </>
          )}
        </>
      )}
      <KeywordDetailDialog row={detail} model={model} days={days} staffView={staffView} onClose={() => setDetail(null)} />
    </div>
  );
}

function RankTable({ rows, model, total, days, onDetail }: { rows: AnRow[]; model: AnalyticsModel; total: boolean; days: number; onDetail: (r: AnRow) => void }) {
  const [sort, setSort] = useState<"word" | "rank" | "total">("rank");
  const [asc, setAsc] = useState(true);
  const heading = (key: typeof sort, label: string) => (
    <button
      className="seo-sort"
      onClick={() => {
        setAsc(sort === key ? !asc : true);
        setSort(key);
      }}
    >
      {label}
      <ArrowDownUp size={12} />
    </button>
  );
  const sortAttr = (key: typeof sort) => (sort === key ? (asc ? "ascending" : "descending") : "none");
  const moves = new Map(model.moves.map((m) => [rowKey(m.row), m]));
  const rankOf = (r: AnRow) => {
    const l = model.latestOf(r.key);
    return l ? (l.found && l.bestRank != null ? l.bestRank : 101) : 999;
  };
  const sorted = [...rows].sort((a, b) => {
    const n = sort === "word" ? a.keyword.localeCompare(b.keyword, "ko") : sort === "total" ? (model.latestOf(a.key)?.total ?? -1) - (model.latestOf(b.key)?.total ?? -1) : rankOf(a) - rankOf(b);
    return asc ? n : -n;
  });
  return (
    <Panel title="키워드 순위" extra={<Badge>{sorted.length}개</Badge>}>
      <Table>
        <thead>
          <tr>
            {total && <th>고객사</th>}
            <th aria-sort={sortAttr("word")}>{heading("word", "키워드")}</th>
            <th>영역</th>
            <th aria-sort={sortAttr("rank")}>{heading("rank", "최고 순위")}</th>
            <th>변동</th>
            <th>최근 {days}일</th>
            <th>우리 항목</th>
            <th aria-sort={sortAttr("total")}>{heading("total", "전체 결과")}</th>
            <th>월 PC / 모바일 검색</th>
            <th>수집 시각</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => {
            const last = model.latestOf(r.key);
            const m = moves.get(r.key);
            return (
              <tr key={r.key}>
                {total && <td>{r.customerName ?? "—"}</td>}
                <td>
                  <button className="seo-text-button" onClick={() => onDetail(r)}>
                    {r.keyword}
                  </button>
                  {r.ref.status !== "tracking" && <small>{STATUS_LABEL[r.ref.status]} · 마지막 수집 결과</small>}
                </td>
                <td>
                  {areaName(r.area)}
                  {r.area === "local" ? " · 상위 5" : ""}
                </td>
                <td>{!last ? "첫 수집 대기" : last.found && last.bestRank != null ? `${last.bestRank}위` : "상위 밖"}</td>
                <td>{m?.isNew ? "신규" : m?.left ? "이탈" : m?.delta == null ? "—" : m.delta > 0 ? `↑ ${m.delta}` : m.delta < 0 ? `↓ ${Math.abs(m.delta)}` : "변동 없음"}</td>
                <td>
                  <Sparkline values={model.historyRaw(r.key)} rank label={`${r.keyword} 순위`} />
                </td>
                <td>{last ? `${last.ourCount} / ${topN(r.area)}` : "—"}</td>
                <td>{last ? num(last.total) : "—"}</td>
                <td>수집 전</td>
                <td>{last ? fmtDateTime(last.collectedAt) : r.ref.status === "tracking" ? "예정" : "수집 기록 없음"}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      {!sorted.length && <p className="la-caption">검색 결과가 없습니다.</p>}
    </Panel>
  );
}

/** 키워드 상세 (시안 KeywordDetail): 영역별 순위 추이·게시 표시, 점유 도넛, 결과 건수 추이, 최신 상위 결과. */
function KeywordDetailDialog({ row, model, days, staffView = false, onClose }: { row: AnRow | null; model: AnalyticsModel; days: number; staffView?: boolean; onClose: () => void }) {
  const h = useSeoHistory(row?.customerId ?? "", row?.keywordId ?? null, days);
  const [showAll, setShowAll] = useState(false);
  const k = row?.ref;
  const area = row?.area ?? "blog";
  const limit = topN(area);
  // 같은 키워드의 영역들을 한 차트의 series 로
  const areaRows = row ? [...new Set(model.daily.filter((d) => d.customerId === row.customerId && d.keywordId === row.keywordId).map((d) => d.area))] : [];
  const series = (areaRows.length ? areaRows : [area]).map((a) => {
    const key = rowKey({ customerId: row?.customerId ?? "", keywordId: row?.keywordId ?? "", area: a });
    return { name: areaName(a), values: model.historyRaw(key), missing: model.history(key).map((v) => (v === 101 ? "상위 밖" : null)) };
  });
  const last = row ? model.latestOf(row.key) : undefined;
  const latest = (h.data?.latest ?? []).find((l) => l.area === area);
  const items = latest?.items ?? [];
  const own10 = items.filter((it) => it.our && it.rank <= 10).length;
  const totals = row ? model.field(row.key, (r) => r.total) : [];
  return (
    <Dialog
      open={!!row}
      onOpenChange={(v) => !v && onClose()}
      title={k?.keyword ?? "키워드 상세"}
      size="detail"
      description={`${areaName(area)} 검색 순위와 최신 수집 결과`}
      footer={<DialogActions secondary={{ label: "닫기", onClick: onClose }} />}
    >
      {row && k ? (
        <div className="la-stack">
          <p className="la-caption">
            {RANK_NOTICE}
            {k.status !== "tracking" && ` · 추적 ${STATUS_LABEL[k.status]} · 과거 수집 이력을 표시합니다.`}
          </p>
          <SeriesChart
            title="영역별 순위 추이"
            labels={model.labels}
            series={series}
            rank
            markers={model.markersFor(row.customerId, row.keyword)}
            description="1위가 위 · 상위 밖과 미수집 구간은 선을 잇지 않습니다 · 세로선은 이 키워드로 게시된 글"
          />
          <div className="la-two">
            <ShareDonut title={`상위 ${limit === 5 ? 5 : 10} 우리 점유`} value={limit === 5 ? (last?.ourCount ?? 0) : own10} total={limit === 5 ? 5 : 10} />
            {limit !== 5 && <ShareDonut title={`상위 ${limit} 우리 점유`} value={last?.ourCount ?? 0} total={limit} />}
          </div>
          <SeriesChart title="검색 결과 전체 건수" format="compact" labels={model.labels} series={[{ name: "결과 건수", values: totals }]} description="검색 API가 반환한 전체 결과 수 · 검색 수요나 예상 유입이 아닙니다" />
          <Panel title="최신 검색 결과" extra={latest ? <Badge>{fmtDateTime(latest.collectedAt)}</Badge> : undefined}>
            <p className="la-caption">
              최신 수집 결과 · 우리 항목을 강조해서 표시합니다.
              {staffView ? ` 요청 작업 ${k.requestCount ?? 1}건 · 최초 ${fmtDateTime(k.firstRequestedAt)}` : ` 추적 시작 ${fmtDateTime(k.firstRequestedAt)}`} · 추적 종료 {fmtDateTime(k.trackingUntil)}
            </p>
            {h.isLoading ? (
              <Loading />
            ) : h.error ? (
              <PanelError error={errorMessage(h.error)} onRetry={() => void h.refetch()} />
            ) : items.length ? (
              <Table>
                <thead>
                  <tr>
                    <th>순위</th>
                    <th>검색 결과</th>
                    <th>발행처</th>
                    <th>구분</th>
                  </tr>
                </thead>
                <tbody>
                  {items.slice(0, showAll ? 30 : 10).map((it) => (
                    <tr key={`${it.rank}-${it.link}`} className={it.our ? "la-own" : ""}>
                      <td>{it.rank}위</td>
                      <td>
                        <a href={it.link} target="_blank" rel="noreferrer">
                          {it.title}
                        </a>
                      </td>
                      <td>{it.author || "—"}</td>
                      <td>{it.our ? <Badge tone="teal">우리 항목</Badge> : "다른 항목"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <Empty>최신 결과가 없습니다.</Empty>
            )}
            {items.length > 10 && (
              <Button variant="ghost" size="sm" onClick={() => setShowAll(!showAll)}>
                {showAll ? "10개만 보기" : "결과 더 보기"}
              </Button>
            )}
          </Panel>
        </div>
      ) : null}
    </Dialog>
  );
}

/* ---------------- 현황 › 검색 성과 요약 (시안 SearchPerformanceSummary) ---------------- */

export function SearchPerformanceSummary({ customerId, tracking, onOpen }: { customerId: string; tracking: number; onOpen: () => void }) {
  const data = useSeoAnalyticsData(useMemo(() => [customerId], [customerId]), 30);
  const model = useMemo(() => analyticsModel(data.daily, data.posts, 30), [data.daily, data.posts]);
  return (
    <section className="la-stack la-summary" aria-label="검색 성과">
      <div className="la-section-heading">
        <div>
          <h3>검색 성과</h3>
          <p>최근 30일 · 네이버 검색 API</p>
        </div>
        <Button size="sm" variant="ghost" onClick={onOpen}>
          분석에서 자세히 보기
          <ArrowUpRight size={14} />
        </Button>
      </div>
      {data.error ? (
        <PanelError error={errorMessage(data.error)} onRetry={data.refetch} />
      ) : data.loading ? (
        <Loading />
      ) : !tracking ? (
        <AnalyticsEmpty />
      ) : !data.daily.length ? (
        <AnalyticsEmpty kind="pending" />
      ) : (
        <>
          <Metrics model={model} summary compare={false} />
          <SeriesChart title="30일 순위 구간 추이" description="날짜별 키워드 수 · 진할수록 상위 순위" kind="bar" stacked labels={model.labels} series={model.bands} markers={model.markers} />
          <Movers model={model} rows={[]} total={false} compact />
        </>
      )}
      <p className="la-caption">
        <CalendarClock size={13} />
        {RANK_NOTICE}
      </p>
    </section>
  );
}

/* ---------------- SEO 비교 (관리자 › 분석) ---------------- */

/** 시안 SeoComparison: 고객사별 추적·노출·상위 10 추이. 행을 누르면 고객사 상세 SEO 탭. */
export function SeoComparisonScreen({ onSelect }: { onSelect: (customerId: string) => void }) {
  const customers = useCustomers("");
  const keywords = useAdminSeoKeywords({});
  const connections = useSeoConnections(null);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("전체");
  const [descending, setDescending] = useState(true);
  const kws = useMemo(() => (keywords.data?.items ?? []) as KeywordRow[], [keywords.data]);
  const ids = useMemo(() => [...new Set(kws.map((k) => k.customerId).filter((x): x is string => !!x))], [kws]);
  const data = useSeoAnalyticsData(ids, 30);
  const models = useMemo(() => new Map(ids.map((id) => [id, analyticsModel(data.daily.filter((r) => r.customerId === id), [], 30)])), [ids, data.daily]);
  const labels = useMemo(() => analyticsModel([], [], 30).labels, []);
  const monthStart = new Date();
  monthStart.setDate(1);
  monthStart.setHours(0, 0, 0, 0);
  const rows = (customers.data?.items ?? [])
    .map((c) => {
      const mine = kws.filter((k) => k.customerId === c.id);
      const m = models.get(c.id);
      const naver = (connections.data ?? []).find((x) => x.customerId === c.id && x.platform === "naver_search");
      const collected = !!m?.daily.length;
      const top10 = m?.metrics[2];
      return {
        id: c.id,
        name: c.name,
        keywords: mine.filter((k) => k.status === "tracking").length,
        newThisMonth: mine.filter((k) => new Date(k.firstRequestedAt) >= monthStart).length,
        exposed: m ? lastValue(m.metrics[1]!.values).value : null,
        top10: top10 ? lastValue(top10.values).value : null,
        top10Values: top10?.values ?? [],
        top10Delta: top10 ? metricDelta(top10) : "—",
        lastCollected: m?.lastCollectedAt ?? null,
        connection: !collected ? "미수집" : !naver || naver.status === "ok" ? "정상" : "연결 확인",
      };
    })
    .filter((c) => c.keywords > 0 || c.lastCollected);
  const shown = rows.filter((c) => c.name.includes(query) && (status === "전체" || c.connection === status)).sort((a, b) => (descending ? b.keywords - a.keywords : a.keywords - b.keywords));
  return (
    <div className="seo-stack">
      <SeoNotice>고객사별 검색 API 수집 결과를 비교합니다. 지역 검색은 상위 5로 별도 집계하며, 미수집 값을 0으로 바꾸지 않습니다.</SeoNotice>
      {data.loading ? <Loading /> : <SeriesChart title="고객사별 상위 10 키워드 추이" labels={labels} series={rows.filter((c) => c.lastCollected).map((c) => ({ name: c.name, values: c.top10Values }))} description={RANK_NOTICE} />}
      <Panel title="고객사 SEO 비교" extra={<Badge tone="slate">{shown.length}개사</Badge>}>
        {customers.error || keywords.error ? (
          <PanelError error={errorMessage(customers.error ?? keywords.error)} onRetry={() => void keywords.refetch()} />
        ) : (
          <>
            <FilterBar>
              <SearchField label="고객사 검색" showLabel placeholder="고객사 이름" value={query} onChange={(e) => setQuery(e.target.value)} />
              <Select label="수집 상태" value={status} onChange={(e) => setStatus(e.target.value)}>
                {["전체", "정상", "연결 확인", "미수집"].map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </Select>
            </FilterBar>
            {customers.isLoading || keywords.isLoading ? (
              <Loading />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <th>고객사</th>
                    <th aria-sort={descending ? "descending" : "ascending"}>
                      <button className="seo-sort" onClick={() => setDescending(!descending)}>
                        추적 키워드
                        <ArrowDownUp size={12} />
                      </button>
                    </th>
                    <th>이번 달 신규</th>
                    <th>상위 100 노출</th>
                    <th>상위 10 / 변동</th>
                    <th>상위 10 추이 · 30일</th>
                    <th>마지막 수집</th>
                    <th>상태</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((c) => (
                    <tr key={c.id}>
                      <td>
                        <button className="seo-text-button" onClick={() => onSelect(c.id)}>
                          {c.name}
                        </button>
                      </td>
                      <td>{c.keywords}</td>
                      <td>{c.newThisMonth}</td>
                      <td>{c.exposed ?? "—"}</td>
                      <td>
                        {c.top10 ?? "—"} / {c.top10Delta}
                      </td>
                      <td>
                        <Sparkline values={c.top10Values} label={`${c.name} 상위 10 추이`} />
                      </td>
                      <td>{c.lastCollected ? fmtDateTime(c.lastCollected) : "—"}</td>
                      <td>
                        <Badge tone={c.connection === "정상" ? "teal" : c.connection === "미수집" ? "slate" : "amber"}>{c.connection}</Badge>
                      </td>
                    </tr>
                  ))}
                  {shown.length === 0 && (
                    <tr>
                      <td colSpan={8}>
                        <Empty>조건에 맞는 고객사가 없습니다.</Empty>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            )}
            <div className="seo-table-foot">
              <p className="seo-caption">지역 검색(최대 5개)은 상위 100·상위 10 집계에서 제외합니다.</p>
            </div>
          </>
        )}
      </Panel>
    </div>
  );
}
